import React from "react";
import { Heart, Github, Linkedin, Mail, ArrowUp, ArrowUpRight } from "lucide-react";
import { personalInfo } from "../mock";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About", href: "#about" },
    { name: "Skills", href: "#skills" },
    { name: "Projects", href: "#projects" },
    { name: "Experience", href: "#experience" },
    { name: "Education", href: "#education" },
    { name: "Contact", href: "#contact" },
  ];

  const socialLinks = [
    { icon: Github, href: personalInfo.github, label: "GitHub" },
    { icon: Linkedin, href: personalInfo.linkedin, label: "LinkedIn" },
    { icon: Mail, href: `mailto:${personalInfo.email}`, label: "Email" },
  ];

  return (
    <footer className="relative border-t border-border/70 bg-secondary/30">
      {/* gradient hairline */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Brand */}
          <div className="space-y-4 text-center md:text-left">
            <a href="#home" className="cursor-target inline-flex items-center gap-3">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 via-emerald-500 to-teal-400 text-white font-display font-bold text-sm shadow-md shadow-teal-500/30">
                AA
              </span>
              <span className="font-display text-lg font-bold">{personalInfo.name}</span>
            </a>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto md:mx-0">
              {personalInfo.title} crafting intelligent automation, agentic AI, and
              business-intelligence solutions that deliver measurable value.
            </p>
            <div className="flex justify-center md:justify-start gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-target border border-border text-muted-foreground hover:text-primary hover:border-primary/50 p-2.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="text-center md:text-left">
            <h4 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-foreground mb-4">
              Navigate
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="cursor-target group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                    <ArrowUpRight
                      size={13}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center md:text-left">
            <h4 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-foreground mb-4">
              Get in touch
            </h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <a href={`mailto:${personalInfo.email}`} className="cursor-target hover:text-primary transition-colors break-all">
                  {personalInfo.email}
                </a>
              </li>
              <li>
                <a href={`tel:${personalInfo.phone}`} className="cursor-target hover:text-primary transition-colors">
                  {personalInfo.phone}
                </a>
              </li>
              <li>{personalInfo.location}</li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-border/60 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs md:text-sm text-muted-foreground flex items-center gap-1.5">
            © {currentYear} Designed & built with
            <Heart size={13} className="text-red-500 fill-red-500 animate-pulse" />
            by {personalInfo.name}. All systems go.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="cursor-target group inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-all duration-300"
          >
            Back to top
            <ArrowUp size={14} className="transition-transform duration-300 group-hover:-translate-y-1" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;